import * as React from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { uploadFile } from "@/services/files";
import { uploadKnowledgeFile } from "@/services/knowledge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import type { ActionConfig, ComponentNode, Page } from "@shared/types";

import { cn } from "@/lib/utils";
import { checkPermission, getCurrentRoles } from "@/lib/builder/permissions";
import { aiChat } from "@/services/ai";
import { applyRecipe } from "@/lib/builder/motion/engine";
import { getPreset } from "@/lib/builder/motion/presets";
import { MotionPresetStyles } from "./motion-styles";
import {
  buildRuntimeProperties,
  DEFAULT_BINDING_FUNCTIONS,
  executeActionSequence,
  getWidgetForType,
  isActionSequenceBinding,
  registerRenderer,
  resolveBinding as resolveBindingExpression,
  resolveProps as resolvePropsExpression,
  setAlertHandler,
  type WidgetRenderer,
} from "@/lib/builder/widgets";
import { useRuntimeData } from "./use-runtime-data";

interface RuntimeContext {
  user?: Record<string, unknown>;
  params?: Record<string, string>;
  permissions?: import("@shared/types").Permission[];
}

type DataCache = Record<string, Record<string, unknown>[]>;

/** 将渲染器运行时上下文转换为绑定引擎上下文（data 由 dataCache 注入） */
function toBindingContext(ctx: RuntimeContext, dataCache: DataCache) {
  return { ...ctx, data: dataCache };
}

function resolveBinding(value: unknown, ctx: RuntimeContext, dataCache: DataCache): unknown {
  return resolveBindingExpression(value, toBindingContext(ctx, dataCache));
}

function resolveProps(
  props: Record<string, unknown>,
  ctx: RuntimeContext,
  dataCache: DataCache,
): Record<string, unknown> {
  return resolvePropsExpression(props, toBindingContext(ctx, dataCache));
}

interface RuntimeErrorBoundaryProps {
  nodeType: string;
  children: React.ReactNode;
}

interface RuntimeErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class RuntimeErrorBoundary extends React.Component<
  RuntimeErrorBoundaryProps,
  RuntimeErrorBoundaryState
> {
  constructor(props: RuntimeErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): RuntimeErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`RuntimeRenderer error in <${this.props.nodeType}>:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded border border-dashed border-destructive bg-destructive/5 p-3 text-xs text-destructive">
          <div className="font-semibold">组件渲染出错：{this.props.nodeType}</div>
          <div className="mt-1 opacity-80">{this.state.error?.message}</div>
        </div>
      );
    }
    return this.props.children;
  }
}

function TextRenderer({ props }: { props: Record<string, unknown> }) {
  const { content, tag, fontSize, color, bold } = props as {
    content: string;
    tag: keyof JSX.IntrinsicElements;
    fontSize?: string;
    color?: string;
    bold?: boolean;
  };
  const Tag = tag || "p";
  return (
    <Tag style={{ fontSize, color }} className={cn("leading-relaxed", bold && "font-semibold")}>
      {String(content ?? "")}
    </Tag>
  );
}

type FormValue = string | number | boolean;

function InputRenderer({
  props,
  onChange,
}: {
  props: Record<string, unknown>;
  onChange?: (name: string, value: FormValue) => void;
}) {
  const { label, placeholder, type, required, name } = props as {
    label?: string;
    placeholder?: string;
    type?: string;
    required?: boolean;
    name?: string;
  };
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive"> *</span>}
        </label>
      )}
      <Input
        type={type}
        placeholder={placeholder}
        name={name}
        required={required}
        onChange={(e) => name && onChange?.(name, e.target.value)}
      />
    </div>
  );
}

function TextareaRenderer({
  props,
  onChange,
}: {
  props: Record<string, unknown>;
  onChange?: (name: string, value: FormValue) => void;
}) {
  const { label, placeholder, required, name, rows } = props as {
    label?: string;
    placeholder?: string;
    required?: boolean;
    name?: string;
    rows?: number;
  };
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive"> *</span>}
        </label>
      )}
      <Textarea
        placeholder={placeholder}
        name={name}
        required={required}
        rows={rows || 3}
        onChange={(e) => name && onChange?.(name, e.target.value)}
      />
    </div>
  );
}

function SelectRenderer({
  props,
  onChange,
}: {
  props: Record<string, unknown>;
  onChange?: (name: string, value: FormValue) => void;
}) {
  const { label, placeholder, required, name, options } = props as {
    label?: string;
    placeholder?: string;
    required?: boolean;
    name?: string;
    options?: { label: string; value: string }[];
  };
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive"> *</span>}
        </label>
      )}
      <Select required={required} onValueChange={(value) => name && onChange?.(name, value)}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder || "请选择"} />
        </SelectTrigger>
        <SelectContent>
          {(options || []).map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SwitchRenderer({
  props,
  onChange,
}: {
  props: Record<string, unknown>;
  onChange?: (name: string, value: FormValue) => void;
}) {
  const { label, required, name } = props as {
    label?: string;
    required?: boolean;
    name?: string;
  };
  return (
    <div className="flex items-center justify-between rounded-md border p-3">
      <label className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <Switch
        required={required}
        onCheckedChange={(checked) => name && onChange?.(name, checked)}
      />
    </div>
  );
}

function CheckboxRenderer({
  props,
  onChange,
}: {
  props: Record<string, unknown>;
  onChange?: (name: string, value: FormValue) => void;
}) {
  const { label, required, name } = props as {
    label?: string;
    required?: boolean;
    name?: string;
  };
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={name}
        required={required}
        onCheckedChange={(checked) => name && onChange?.(name, checked === true)}
      />
      <Label htmlFor={name} className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
    </div>
  );
}

function RadioRenderer({
  props,
  onChange,
}: {
  props: Record<string, unknown>;
  onChange?: (name: string, value: FormValue) => void;
}) {
  const { label, required, name, options } = props as {
    label?: string;
    required?: boolean;
    name?: string;
    options?: { label: string; value: string }[];
  };
  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive"> *</span>}
        </label>
      )}
      <RadioGroup
        required={required}
        onValueChange={(value) => name && onChange?.(name, value)}
      >
        {(options || []).map((opt) => (
          <div key={opt.value} className="flex items-center gap-2">
            <RadioGroupItem value={opt.value} id={`${name}-${opt.value}`} />
            <Label htmlFor={`${name}-${opt.value}`} className="text-sm font-normal">
              {opt.label}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}

function DatePickerRenderer({
  props,
  onChange,
}: {
  props: Record<string, unknown>;
  onChange?: (name: string, value: FormValue) => void;
}) {
  const { label, required, name } = props as {
    label?: string;
    required?: boolean;
    name?: string;
  };
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive"> *</span>}
        </label>
      )}
      <DatePicker
        name={name}
        required={required}
        onChange={(e) => name && onChange?.(name, e.target.value)}
      />
    </div>
  );
}

function TableRenderer({
  props,
  permissions,
}: {
  props: Record<string, unknown>;
  permissions?: import("@shared/types").Permission[];
}) {
  const { resource, columns, pageSize } = props as {
    resource?: string;
    columns?: { field: string; title: string }[];
    pageSize?: number;
  };
  const roles = getCurrentRoles();
  const canRead = resource ? checkPermission(roles, resource, "read", permissions || []) : true;
  const canUpdate = resource ? checkPermission(roles, resource, "update", permissions || []) : true;
  const canDelete = resource ? checkPermission(roles, resource, "delete", permissions || []) : true;
  const { getData, refresh, update, deleteOne } = useRuntimeData();
  const rows = resource ? getData(resource) : undefined;
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editValues, setEditValues] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (resource && canRead) {
      void refresh(resource);
    }
  }, [resource, refresh, canRead]);

  if (!canRead) {
    return (
      <div className="rounded-md border p-4 text-sm text-muted-foreground">
        无权限查看此数据表。
      </div>
    );
  }

  const displayColumns = columns && columns.length > 0 ? columns : (rows && rows.length > 0 ? Object.keys(rows[0]).map((k) => ({ field: k, title: k })) : []);
  const idField = "id";

  const startEdit = (row: Record<string, unknown>) => {
    setEditingId(String(row[idField]));
    const values: Record<string, string> = {};
    displayColumns.forEach((col) => {
      if (col.field !== idField) {
        values[col.field] = String(row[col.field] ?? "");
      }
    });
    setEditValues(values);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const saveEdit = async (rowId: string) => {
    if (!resource) return;
    try {
      await update(resource, rowId, editValues);
      setEditingId(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("更新失败", err);
    }
  };

  const handleDelete = async (rowId: string) => {
    if (!resource) return;
    if (!confirm("确定删除这条记录吗？")) return;
    try {
      await deleteOne(resource, rowId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("删除失败", err);
    }
  };

  return (
    <div className="rounded-md border">
      <div className="border-b bg-muted px-4 py-2 text-sm font-medium">数据表：{resource || "未配置"}</div>
      {!rows ? (
        <div className="p-4 text-sm text-muted-foreground">加载中...</div>
      ) : rows.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground">暂无数据</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {displayColumns.map((col) => (
                <TableHead key={col.field}>{col.title || col.field}</TableHead>
              ))}
              {(canUpdate || canDelete) && <TableHead className="w-32">操作</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, pageSize || 10).map((row, idx) => {
              const rowId = String(row[idField] ?? idx);
              const isEditing = editingId === rowId;
              return (
                <TableRow key={rowId}>
                  {displayColumns.map((col) => (
                    <TableCell key={col.field}>
                      {isEditing && col.field !== idField ? (
                        <Input
                          value={editValues[col.field] ?? ""}
                          onChange={(e) =>
                            setEditValues((prev) => ({ ...prev, [col.field]: e.target.value }))
                          }
                          className="h-7 text-xs"
                        />
                      ) : (
                        String(row[col.field] ?? "")
                      )}
                    </TableCell>
                  ))}
                  {(canUpdate || canDelete) && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {canUpdate &&
                          (isEditing ? (
                            <>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => saveEdit(rowId)}>
                                保存
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={cancelEdit}>
                                取消
                              </Button>
                            </>
                          ) : (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => startEdit(row)}>
                              编辑
                            </Button>
                          ))}
                        {canDelete && !isEditing && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive" onClick={() => handleDelete(rowId)}>
                            删除
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function AIChatRenderer({ props }: { props: Record<string, unknown> }) {
  const { welcomeMessage, agentId } = props as { welcomeMessage?: string; agentId?: string };
  const [messages, setMessages] = React.useState<{ role: string; content: string }[]>([
    { role: "assistant", content: welcomeMessage || "你好，有什么可以帮你的？" },
  ]);
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const bottomRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;
    const text = input;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);
    try {
      const result = await aiChat(text, agentId);
      const response = result.response;
      const content =
        typeof response.content === "string"
          ? response.content
          : JSON.stringify(response, null, 2);
      setMessages((prev) => [...prev, { role: "assistant", content }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `请求失败：${String(error)}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="flex h-80 flex-col">
      <CardHeader>
        <CardTitle className="text-base">AI 对话</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 space-y-2 overflow-auto">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`rounded px-3 py-2 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground self-end" : "bg-muted"}`}
          >
            {m.content}
          </div>
        ))}
        {loading && <div className="rounded bg-muted px-3 py-2 text-sm text-muted-foreground">思考中...</div>}
        <div ref={bottomRef} />
      </CardContent>
      <div className="flex gap-2 border-t p-3">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="输入消息..."
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleSend();
            }
          }}
        />
        <Button size="sm" disabled={loading || !input.trim()} onClick={() => void handleSend()}>
          发送
        </Button>
      </div>
    </Card>
  );
}

function ChartRenderer({ props }: { props: Record<string, unknown> }) {
  const { type, resource } = props as { type?: string; resource?: string };
  return (
    <div className="flex h-48 items-center justify-center rounded-md border bg-muted text-sm text-muted-foreground">
      [{type || "bar"} 图表] 数据源：{resource || "未配置"}
    </div>
  );
}

function RichTextRenderer({ props }: { props: Record<string, unknown> }) {
  const { content } = props as { content?: string };
  return (
    <div
      className="prose prose-sm max-w-none dark:prose-invert"
      dangerouslySetInnerHTML={{ __html: content || "" }}
    />
  );
}

function FileUploadRenderer({ props }: { props: Record<string, unknown> }) {
  const { target, accept } = props as { target?: string; accept?: string };
  const [uploading, setUploading] = React.useState(false);
  const [result, setResult] = React.useState<string | null>(null);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      if (target === "knowledge") {
        const data = await uploadKnowledgeFile(file);
        setResult(`已上传知识库：${data.document_id}，${data.chunks} 个切片`);
      } else {
        const data = await uploadFile(file);
        setResult(`已上传文件：${data.key}`);
      }
    } catch (error) {
      setResult(`上传失败：${String(error)}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-md border p-4">
      <label className="text-sm font-medium">{target === "knowledge" ? "上传知识库文档" : "上传文件"}</label>
      <Input
        type="file"
        accept={accept || ".txt,.md"}
        onChange={(e) => void handleChange(e)}
        disabled={uploading}
        className="mt-2 text-xs"
      />
      {uploading && <p className="mt-2 text-xs text-muted-foreground">上传中...</p>}
      {result && <p className="mt-2 text-xs text-green-600">{result}</p>}
    </div>
  );
}

function ContainerRenderer({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { direction, gap, padding, background } = props as {
    direction?: "row" | "column";
    gap?: string;
    padding?: string;
    background?: string;
  };
  return (
    <div style={{ gap, padding, background }} className={cn("flex", direction === "row" ? "flex-row" : "flex-col")}>
      {children}
    </div>
  );
}

function CardRenderer({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { title } = props as { title?: string };
  return (
    <Card>
      {title && (
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function FormRenderer({
  props,
  permissions,
  children,
}: {
  props: Record<string, unknown>;
  permissions?: import("@shared/types").Permission[];
  children?: React.ReactNode;
}) {
  const { submitLabel, resource } = props as { submitLabel?: string; resource?: string };
  const canCreate = resource
    ? checkPermission(getCurrentRoles(), resource, "create", permissions || [])
    : true;
  const { create } = useRuntimeData();
  const [formValues, setFormValues] = React.useState<Record<string, FormValue>>({});

  const handleChange = (name: string, value: FormValue) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  if (!canCreate) {
    return (
      <div className="rounded-lg border p-4 text-sm text-muted-foreground">
        无权限提交此表单。
      </div>
    );
  }

  return (
    <form
      className="space-y-4 rounded-lg border p-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!resource) return;
        try {
          await create(resource, formValues);
          setFormValues({});
          // Reset native form controls
          const formEl = e.currentTarget;
          formEl.reset();
          // eslint-disable-next-line no-console
          console.log("提交成功", formValues);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("提交失败", err);
        }
      }}
    >
      {children && React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<{ onFormChange?: (name: string, value: FormValue) => void }>, {
            onFormChange: handleChange,
          });
        }
        return child;
      })}
      <Button type="submit">{submitLabel || "提交"}</Button>
    </form>
  );
}

// ------------------------------------------------------------
// 6.7 扩展组件渲染器（layout / basic / data / ai / chart / content / motion）
// 均保持现有风格：函数组件 + resolveProps
// ------------------------------------------------------------

function GridRenderer({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { columns, gap, padding } = props as { columns?: number; gap?: string; padding?: string };
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${Math.max(1, Number(columns || 3))}, minmax(0, 1fr))`,
        gap,
        padding,
      }}
    >
      {children}
    </div>
  );
}

function FlexRenderer({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { direction, justify, align, gap, wrap } = props as {
    direction?: string;
    justify?: string;
    align?: string;
    gap?: string;
    wrap?: boolean;
  };
  return (
    <div
      style={{
        display: "flex",
        flexDirection: direction === "column" ? "column" : "row",
        justifyContent: justify,
        alignItems: align,
        gap,
        flexWrap: wrap ? "wrap" : "nowrap",
      }}
    >
      {children}
    </div>
  );
}

function StackRenderer({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { direction, spacing, align } = props as {
    direction?: string;
    spacing?: string;
    align?: string;
  };
  return (
    <div
      style={{
        display: "flex",
        flexDirection: direction === "row" ? "row" : "column",
        gap: spacing,
        alignItems: align,
      }}
    >
      {children}
    </div>
  );
}

function TabsRenderer({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { tabs, defaultValue } = props as {
    tabs?: { label: string; value: string }[];
    defaultValue?: string;
  };
  const tabList = tabs && tabs.length > 0 ? tabs : [{ label: "标签1", value: "tab1" }];
  const [active, setActive] = React.useState(defaultValue || tabList[0].value);
  return (
    <div>
      <Tabs value={active} onValueChange={setActive}>
        <TabsList>
          {tabList.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function SidebarRenderer({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { title, items, collapsed } = props as {
    title?: string;
    items?: { label: string; path?: string; icon?: string }[];
    collapsed?: boolean;
  };
  return (
    <div
      className={cn(
        "flex h-full flex-col border-r bg-muted/40",
        collapsed ? "w-14" : "w-56",
      )}
    >
      {title && !collapsed && <div className="px-4 py-3 text-sm font-semibold">{title}</div>}
      <nav className="flex-1 space-y-1 p-2">
        {(items || []).map((item) => (
          <a
            key={item.path || item.label}
            href={item.path || "#"}
            onClick={(e) => e.preventDefault()}
            className="flex items-center gap-2 rounded px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            title={item.label}
          >
            {item.icon && <span className="text-xs">{item.icon}</span>}
            {!collapsed && <span>{item.label}</span>}
          </a>
        ))}
      </nav>
      {children && <div className="border-t p-2">{children}</div>}
    </div>
  );
}

function SliderRenderer({
  props,
  onChange,
}: {
  props: Record<string, unknown>;
  onChange?: (name: string, value: FormValue) => void;
}) {
  const { label, min, max, step, defaultValue, name } = props as {
    label?: string;
    min?: number;
    max?: number;
    step?: number;
    defaultValue?: number;
    name?: string;
  };
  const [value, setValue] = React.useState(Number(defaultValue ?? 0));
  return (
    <div className="space-y-1.5">
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">{label}</label>
          <span className="text-xs text-muted-foreground">{value}</span>
        </div>
      )}
      <input
        type="range"
        name={name}
        min={min ?? 0}
        max={max ?? 100}
        step={step ?? 1}
        value={value}
        onChange={(e) => {
          const next = Number(e.target.value);
          setValue(next);
          if (name) onChange?.(name, next);
        }}
        className="w-full"
      />
    </div>
  );
}

/** 图表数据源 hook：拉取并缓存数据表行 */
function useChartRows(resource?: string) {
  const { getData, refresh } = useRuntimeData();
  const rows = resource ? getData(resource) : undefined;
  React.useEffect(() => {
    if (resource) void refresh(resource);
  }, [resource, refresh]);
  return rows;
}

function toChartPoints(
  rows: Record<string, unknown>[] | undefined,
  xField: string | undefined,
  yField: string | undefined,
): { x: string; y: number }[] {
  return (rows || []).map((row) => ({
    x: String(row[xField || ""] ?? ""),
    y: Number(row[yField || ""] ?? 0),
  }));
}

function ChartShell({
  title,
  height,
  children,
}: {
  title: string;
  height: number;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 text-xs text-muted-foreground">{title}</div>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 480 ${height}`}
        preserveAspectRatio="none"
        className="block"
      >
        {children}
      </svg>
    </div>
  );
}

function BarChartRenderer({ props }: { props: Record<string, unknown> }) {
  const { resource, xField, yField, height, color } = props as {
    resource?: string;
    xField?: string;
    yField?: string;
    height?: number;
    color?: string;
  };
  const rows = useChartRows(resource);
  const points = toChartPoints(rows, xField, yField);
  const h = Number(height || 240);
  const pad = 28;
  const innerH = h - pad - 10;
  const maxY = Math.max(1, ...points.map((p) => p.y));
  const step = points.length > 0 ? (480 - pad) / points.length : 480;
  const barW = Math.max(4, step * 0.6);
  return (
    <ChartShell title={`柱状图：${resource || "未配置数据源"}`} height={h}>
      {points.map((p, i) => {
        const bh = (p.y / maxY) * innerH;
        return (
          <rect
            key={i}
            x={pad + i * step + (step - barW) / 2}
            y={h - 10 - bh}
            width={barW}
            height={bh}
            fill={color || "#0ea5e9"}
            rx={2}
          />
        );
      })}
    </ChartShell>
  );
}

function LineChartRenderer({ props }: { props: Record<string, unknown> }) {
  const { resource, xField, yField, height, color } = props as {
    resource?: string;
    xField?: string;
    yField?: string;
    height?: number;
    color?: string;
  };
  const rows = useChartRows(resource);
  const points = toChartPoints(rows, xField, yField);
  const h = Number(height || 240);
  const pad = 28;
  const innerH = h - pad - 10;
  const maxY = Math.max(1, ...points.map((p) => p.y));
  const step = points.length > 1 ? (480 - pad) / (points.length - 1) : 0;
  const linePoints = points
    .map((p, i) => `${pad + i * step},${h - 10 - (p.y / maxY) * innerH}`)
    .join(" ");
  return (
    <ChartShell title={`折线图：${resource || "未配置数据源"}`} height={h}>
      <polyline points={linePoints} fill="none" stroke={color || "#0ea5e9"} strokeWidth={2} />
    </ChartShell>
  );
}

function AreaChartRenderer({ props }: { props: Record<string, unknown> }) {
  const { resource, xField, yField, height, color } = props as {
    resource?: string;
    xField?: string;
    yField?: string;
    height?: number;
    color?: string;
  };
  const rows = useChartRows(resource);
  const points = toChartPoints(rows, xField, yField);
  const h = Number(height || 240);
  const pad = 28;
  const innerH = h - pad - 10;
  const maxY = Math.max(1, ...points.map((p) => p.y));
  const step = points.length > 1 ? (480 - pad) / (points.length - 1) : 0;
  const coords = points.map((p, i) => ({
    x: pad + i * step,
    y: h - 10 - (p.y / maxY) * innerH,
  }));
  const linePoints = coords.map((c) => `${c.x},${c.y}`).join(" ");
  const areaPoints =
    coords.length > 0
      ? `${pad},${h - 10} ${linePoints} ${coords[coords.length - 1].x},${h - 10}`
      : "";
  return (
    <ChartShell title={`面积图：${resource || "未配置数据源"}`} height={h}>
      {areaPoints && (
        <polygon points={areaPoints} fill={color || "#0ea5e9"} opacity={0.25} stroke="none" />
      )}
      <polyline points={linePoints} fill="none" stroke={color || "#0ea5e9"} strokeWidth={2} />
    </ChartShell>
  );
}

const PIE_COLORS = ["#0ea5e9", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444", "#14b8a6", "#f97316", "#64748b"];

function PieChartRenderer({ props }: { props: Record<string, unknown> }) {
  const { resource, labelField, valueField, height } = props as {
    resource?: string;
    labelField?: string;
    valueField?: string;
    height?: number;
  };
  const rows = useChartRows(resource);
  const data = (rows || []).map((row) => ({
    label: String(row[labelField || ""] ?? ""),
    value: Number(row[valueField || ""] ?? 0),
  }));
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const cx = 120;
  const cy = 110;
  const r = 90;
  let angle = -Math.PI / 2;
  const arcs = data.map((d, i) => {
    const start = angle;
    const end = angle + (d.value / total) * Math.PI * 2;
    angle = end;
    const largeArc = end - start > Math.PI ? 1 : 0;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    return (
      <path
        key={i}
        d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`}
        fill={PIE_COLORS[i % PIE_COLORS.length]}
      />
    );
  });
  const h = Number(height || 240);
  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 text-xs text-muted-foreground">饼图：{resource || "未配置数据源"}</div>
      <div className="flex items-center gap-4">
        <svg width={200} height={h} viewBox="0 0 240 220" className="shrink-0">
          {arcs}
        </svg>
        <ul className="min-w-0 space-y-1 text-xs">
          {data.map((d, i) => (
            <li key={i} className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
              />
              <span className="truncate text-muted-foreground">
                {d.label || `数据 ${i + 1}`}：{d.value}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MetricCardRenderer({ props }: { props: Record<string, unknown> }) {
  const { label, resource, field, aggregate, prefix, suffix, color } = props as {
    label?: string;
    resource?: string;
    field?: string;
    aggregate?: string;
    prefix?: string;
    suffix?: string;
    color?: string;
  };
  const rows = useChartRows(resource);
  const values = (rows || []).map((row) => Number(row[field || ""] ?? 0));
  let value: number;
  switch (aggregate || "sum") {
    case "avg":
      value = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0;
      break;
    case "count":
      value = (rows || []).length;
      break;
    case "max":
      value = values.length ? Math.max(...values) : 0;
      break;
    case "min":
      value = values.length ? Math.min(...values) : 0;
      break;
    case "last":
      value = values[values.length - 1] ?? 0;
      break;
    default:
      value = values.reduce((s, v) => s + v, 0);
  }
  const display = Number.isFinite(value)
    ? Number.isInteger(value)
      ? String(value)
      : value.toFixed(2)
    : "0";
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label || "指标"}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="text-2xl font-bold" style={{ color: color || "#0f172a" }}>
          {prefix}
          {display}
          {suffix}
        </div>
      </CardContent>
    </Card>
  );
}

function ListRenderer({
  props,
  permissions,
}: {
  props: Record<string, unknown>;
  permissions?: import("@shared/types").Permission[];
}) {
  const { resource, field, limit, header } = props as {
    resource?: string;
    field?: string;
    limit?: number;
    header?: string;
  };
  const roles = getCurrentRoles();
  const canRead = resource ? checkPermission(roles, resource, "read", permissions || []) : true;
  const { getData, refresh } = useRuntimeData();
  const rows = resource ? getData(resource) : undefined;

  React.useEffect(() => {
    if (resource && canRead) void refresh(resource);
  }, [resource, refresh, canRead]);

  if (!canRead) {
    return <div className="rounded-md border p-4 text-sm text-muted-foreground">无权限查看此数据表。</div>;
  }
  return (
    <div className="rounded-md border">
      <div className="border-b bg-muted px-4 py-2 text-sm font-medium">{header || "数据列表"}</div>
      {!rows ? (
        <div className="p-4 text-sm text-muted-foreground">加载中...</div>
      ) : rows.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground">暂无数据</div>
      ) : (
        <ul className="divide-y">
          {rows.slice(0, Number(limit || 10)).map((row, idx) => (
            <li key={idx} className="px-4 py-2 text-sm">
              {String(row[field || "name"] ?? JSON.stringify(row))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PaginationRenderer({ props }: { props: Record<string, unknown> }) {
  const { resource, pageSize, current, total } = props as {
    resource?: string;
    pageSize?: number;
    current?: number;
    total?: number;
  };
  const { getData, refresh } = useRuntimeData();
  const rows = resource ? getData(resource) : undefined;
  const [page, setPage] = React.useState(Number(current || 1));

  React.useEffect(() => {
    if (resource) void refresh(resource);
  }, [resource, refresh]);

  const totalCount = Number(total || (rows?.length ?? 0));
  const pageCount = Math.max(1, Math.ceil(totalCount / Math.max(1, Number(pageSize || 10))));
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);

  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs"
        disabled={page <= 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
      >
        上一页
      </Button>
      {pages.map((p) => (
        <Button
          key={p}
          variant={p === page ? "default" : "ghost"}
          size="sm"
          className="h-7 w-7 px-0 text-xs"
          onClick={() => setPage(p)}
        >
          {p}
        </Button>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs"
        disabled={page >= pageCount}
        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
      >
        下一页
      </Button>
      <span className="ml-2 text-xs text-muted-foreground">共 {totalCount} 条</span>
    </div>
  );
}

function SearchBarRenderer({
  props,
  onChange,
}: {
  props: Record<string, unknown>;
  onChange?: (name: string, value: FormValue) => void;
}) {
  const { placeholder, value } = props as { placeholder?: string; value?: string };
  const [text, setText] = React.useState(String(value ?? ""));
  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={text}
        placeholder={placeholder || "搜索..."}
        onChange={(e) => {
          setText(e.target.value);
          onChange?.("searchValue", e.target.value);
        }}
        className="pl-9"
      />
    </div>
  );
}

function FilterPanelRenderer({ props }: { props: Record<string, unknown> }) {
  const { fields } = props as {
    fields?: { name: string; label: string; type: string; options?: string[] }[];
  };
  const [values, setValues] = React.useState<Record<string, string>>({});
  return (
    <div className="grid gap-3 rounded-md border p-3 sm:grid-cols-2 lg:grid-cols-3">
      {(fields || []).map((field) => {
        if (field.type === "select" && field.options) {
          return (
            <div key={field.name} className="space-y-1">
              <Label className="text-xs">{field.label}</Label>
              <Select
                value={values[field.name] ?? ""}
                onValueChange={(v) => setValues((prev) => ({ ...prev, [field.name]: v }))}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="全部" />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }
        return (
          <div key={field.name} className="space-y-1">
            <Label className="text-xs">{field.label}</Label>
            <Input
              className="h-8 text-xs"
              value={values[field.name] ?? ""}
              placeholder={field.label}
              onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
            />
          </div>
        );
      })}
      <div className="flex items-end">
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          onClick={() => setValues({})}
        >
          重置
        </Button>
      </div>
    </div>
  );
}

function AICopilotRenderer({ props }: { props: Record<string, unknown> }) {
  const { placeholder, agentId } = props as { placeholder?: string; agentId?: string };
  const [input, setInput] = React.useState("");
  const [result, setResult] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const handleAsk = async () => {
    if (!input.trim()) return;
    setLoading(true);
    try {
      const res = await aiChat(`请简要回答：${input}`, agentId);
      const content =
        typeof res.response.content === "string"
          ? res.response.content
          : JSON.stringify(res.response, null, 2);
      setResult(content);
    } catch (err) {
      setResult(`请求失败：${String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex gap-2">
        <Input
          value={input}
          placeholder={placeholder || "输入问题，AI 助手为你解答..."}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleAsk();
            }
          }}
        />
        <Button size="sm" disabled={loading || !input.trim()} onClick={() => void handleAsk()}>
          提问
        </Button>
      </div>
      {loading && <p className="text-xs text-muted-foreground">思考中...</p>}
      {result && <div className="rounded bg-muted px-3 py-2 text-sm">{result}</div>}
    </div>
  );
}

function AIGenerateRenderer({ props }: { props: Record<string, unknown> }) {
  const { prompt, agentId } = props as { prompt?: string; agentId?: string };
  const [input, setInput] = React.useState(String(prompt ?? ""));
  const [output, setOutput] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const handleGenerate = async () => {
    if (!input.trim()) return;
    setLoading(true);
    try {
      const res = await aiChat(input, agentId);
      const content =
        typeof res.response.content === "string"
          ? res.response.content
          : JSON.stringify(res.response, null, 2);
      setOutput(content);
    } catch (err) {
      setOutput(`生成失败：${String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border p-3">
      <Textarea
        value={input}
        rows={4}
        placeholder="描述要生成的内容..."
        onChange={(e) => setInput(e.target.value)}
      />
      <Button size="sm" disabled={loading || !input.trim()} onClick={() => void handleGenerate()}>
        {loading ? "生成中..." : "生成"}
      </Button>
      {output && <pre className="whitespace-pre-wrap rounded bg-muted p-3 text-xs">{output}</pre>}
    </div>
  );
}

function ImageRenderer({ props }: { props: Record<string, unknown> }) {
  const { src, alt, objectFit, width, height, rounded } = props as {
    src?: string;
    alt?: string;
    objectFit?: string;
    width?: string;
    height?: string;
    rounded?: boolean;
  };
  if (!src) {
    return (
      <div className="flex h-32 items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
        图片组件：配置 src
      </div>
    );
  }
  return (
    <img
      src={String(src)}
      alt={String(alt ?? "")}
      style={{ objectFit: (objectFit as never) || "cover", width, height }}
      className={cn("block", rounded && "rounded-lg")}
    />
  );
}

function VideoRenderer({ props }: { props: Record<string, unknown> }) {
  const { src, poster, controls, autoplay, muted } = props as {
    src?: string;
    poster?: string;
    controls?: boolean;
    autoplay?: boolean;
    muted?: boolean;
  };
  if (!src) {
    return (
      <div className="flex h-32 items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
        视频组件：配置 src
      </div>
    );
  }
  return (
    <video
      src={String(src)}
      poster={poster}
      controls={controls !== false}
      autoPlay={Boolean(autoplay)}
      muted={Boolean(muted)}
      className="w-full"
    />
  );
}

// 轻量 Markdown 解析（自写，不引第三方库）：标题 / 代码块 / 列表 / 引用 / 段落 / 行内加粗/斜体/行内代码/链接
function tokenizeInline(text: string): { kind: string; value: string; href?: string }[] {
  const tokens: { kind: string; value: string; href?: string }[] = [];
  const pattern =
    /(\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|_[^_\n]+_|`[^`]+`|\[[^\]\n]+\]\([^)\n]+\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ kind: "text", value: text.slice(lastIndex, match.index) });
    }
    const token = match[0];
    if (token.startsWith("**") && token.endsWith("**")) {
      tokens.push({ kind: "bold", value: token.slice(2, -2) });
    } else if (token.startsWith("__") && token.endsWith("__")) {
      tokens.push({ kind: "bold", value: token.slice(2, -2) });
    } else if (token.startsWith("`") && token.endsWith("`")) {
      tokens.push({ kind: "code", value: token.slice(1, -1) });
    } else if (token.startsWith("[")) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch) {
        tokens.push({ kind: "link", value: linkMatch[1], href: linkMatch[2] });
      } else {
        tokens.push({ kind: "text", value: token });
      }
    } else if (token.startsWith("*") && token.endsWith("*")) {
      tokens.push({ kind: "italic", value: token.slice(1, -1) });
    } else if (token.startsWith("_") && token.endsWith("_")) {
      tokens.push({ kind: "italic", value: token.slice(1, -1) });
    } else {
      tokens.push({ kind: "text", value: token });
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    tokens.push({ kind: "text", value: text.slice(lastIndex) });
  }
  return tokens;
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  return tokenizeInline(text).map((token, i) => {
    const key = `${keyPrefix}-${i}`;
    switch (token.kind) {
      case "bold":
        return <strong key={key}>{token.value}</strong>;
      case "italic":
        return <em key={key}>{token.value}</em>;
      case "code":
        return (
          <code key={key} className="rounded bg-muted px-1 py-0.5 text-xs">
            {token.value}
          </code>
        );
      case "link":
        return (
          <a
            key={key}
            href={token.href || "#"}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline"
          >
            {token.value}
          </a>
        );
      default:
        return <span key={key}>{token.value}</span>;
    }
  });
}

function renderMarkdownBlocks(source: string): React.ReactNode[] {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (/^```/.test(line.trim())) {
      const buf: string[] = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        buf.push(lines[i]);
        i += 1;
      }
      i += 1; // 跳过闭合围栏
      nodes.push(
        <pre key={key++} className="overflow-auto rounded-md bg-muted p-3 text-xs">
          <code>{buf.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = Math.min(heading[1].length, 6) as 1 | 2 | 3 | 4 | 5 | 6;
      const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
      nodes.push(
        <Tag key={key++} className="mt-3 mb-2 font-semibold">
          {renderInline(heading[2], `h${key}`)}
        </Tag>,
      );
      i += 1;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i += 1;
      }
      nodes.push(
        <ul key={key++} className="list-disc pl-5">
          {items.map((item, idx) => (
            <li key={idx}>{renderInline(item, `li${key}-${idx}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^\s*>\s?/, ""));
        i += 1;
      }
      nodes.push(
        <blockquote key={key++} className="border-l-2 border-muted pl-3 text-muted-foreground">
          {renderInline(buf.join(" "), `bq${key}`)}
        </blockquote>,
      );
      continue;
    }

    if (line.trim() === "") {
      i += 1;
      continue;
    }

    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^```/.test(lines[i].trim()) &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*>\s?/.test(lines[i])
    ) {
      para.push(lines[i]);
      i += 1;
    }
    nodes.push(
      <p key={key++} className="my-2">
        {renderInline(para.join(" "), `p${key}`)}
      </p>,
    );
  }
  return nodes;
}

function MarkdownRenderer({ props }: { props: Record<string, unknown> }) {
  const { content } = props as { content?: string };
  return (
    <div className="prose prose-sm max-w-none dark:prose-invert">
      {renderMarkdownBlocks(content ?? "")}
    </div>
  );
}

// ------------------------------------------------------------
// Motion 组件（自包含 CSS 动效；动效引擎由并行任务实现，接口可无缝替换）
// ------------------------------------------------------------

const MOTION_CSS = `
@keyframes dp-motion-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes dp-motion-slide-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
@keyframes dp-motion-slide-in-left { from { opacity: 0; transform: translateX(-24px); } to { opacity: 1; transform: translateX(0); } }
@keyframes dp-motion-scale-in { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
.dp-motion { animation-fill-mode: both; }
.dp-motion-none { animation: none; }
.dp-motion-fade-in { animation-name: dp-motion-fade-in; }
.dp-motion-slide-up { animation-name: dp-motion-slide-up; }
.dp-motion-slide-in-left { animation-name: dp-motion-slide-in-left; }
.dp-motion-scale-in { animation-name: dp-motion-scale-in; }
`;

function motionClassName(animation: string | undefined): string {
  const key = animation && animation !== "none" ? `dp-motion-${animation}` : "dp-motion-none";
  return cn("dp-motion", key);
}

function MotionGroupRenderer({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { animation, duration, delay, stagger } = props as {
    animation?: string;
    duration?: number;
    delay?: number;
    stagger?: number;
  };
  const dur = Number(duration || 400);
  const baseDelay = Number(delay || 0);
  const staggerMs = Number(stagger || 0);
  return (
    <>
      <style>{MOTION_CSS}</style>
      <div
        className={motionClassName(animation)}
        style={{ animationDuration: `${dur}ms`, animationDelay: `${baseDelay}ms` }}
      >
        {children &&
          React.Children.map(children, (child, idx) =>
            React.isValidElement(child)
              ? React.cloneElement(child as React.ReactElement<{ style?: React.CSSProperties }>, {
                  style: {
                    ...(child.props.style ?? {}),
                    animationDelay: `${baseDelay + idx * staggerMs}ms`,
                  },
                })
              : child,
          )}
      </div>
    </>
  );
}

function AnimatedRouteRenderer({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { animation, duration, direction } = props as {
    animation?: string;
    duration?: number;
    direction?: string;
  };
  const dur = Number(duration || 350);
  return (
    <>
      <style>{MOTION_CSS}</style>
      <div
        key={direction || "up"}
        className={motionClassName(animation)}
        style={{ animationDuration: `${dur}ms` }}
      >
        {children}
      </div>
    </>
  );
}

function ScrollRevealRenderer({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { animation, delay, threshold, once } = props as {
    animation?: string;
    delay?: number;
    threshold?: number;
    once?: boolean;
  };
  const ref = React.useRef<HTMLDivElement>(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once !== false) observer.unobserve(entry.target);
          } else if (once === false) {
            setVisible(false);
          }
        });
      },
      { threshold: Number(threshold || 0.2) },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold]);

  return (
    <>
      <style>{MOTION_CSS}</style>
      <div
        ref={ref}
        className={visible ? motionClassName(animation) : "opacity-0"}
        style={{
          animationDuration: "500ms",
          animationDelay: `${Number(delay || 0)}ms`,
        }}
      >
        {children}
      </div>
    </>
  );
}

const renderers: Record<
  string,
  React.FC<{
    node: ComponentNode;
    ctx: RuntimeContext;
    dataCache: Record<string, Record<string, unknown>[]>;
    children?: React.ReactNode;
    onFormChange?: (name: string, value: FormValue) => void;
  }>
> = {
  Page: ({ children }) => <div className="min-h-full p-4">{children}</div>,
  Container: ({ node, ctx, dataCache, children }) => (
    <ContainerRenderer props={resolveProps(node.props, ctx, dataCache)}>{children}</ContainerRenderer>
  ),
  Text: ({ node, ctx, dataCache }) => <TextRenderer props={resolveProps(node.props, ctx, dataCache)} />,
  Button: ({ node, ctx, dataCache }) => {
    const navigate = useNavigate();
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [modalConfig, setModalConfig] = React.useState<{ title?: string; content?: string }>({});
    const { label, variant, size } = resolveProps(node.props, ctx, dataCache) as Record<string, string>;

    const executeAction = (action: ActionConfig) => {
      if (action.type === "navigate") {
        navigate(String(action.config.path || "/"));
      } else if (action.type === "openModal") {
        setModalConfig({
          title: resolveBinding(String(action.config.title || "提示"), ctx, dataCache) as string,
          content: resolveBinding(String(action.config.content || ""), ctx, dataCache) as string,
        });
        setModalOpen(true);
      } else if (action.type === "submitForm") {
        const form = buttonRef.current?.closest("form");
        if (form) {
          form.requestSubmit();
        }
      }
    };

    const handleClick = () => {
      const binding = node.events?.find((e) => e.event === "onClick");
      if (binding) {
        binding.actions.forEach((action) => executeAction(action));
      }
      // 6.5 动作触发器绑定：{{ showAlert('保存成功', 'info'); saveCustomer(); }}
      const trigger = node.props.onClick;
      if (isActionSequenceBinding(trigger)) {
        try {
          executeActionSequence(
            String(trigger).replace(/^\{\{\s*/, "").replace(/\s*\}\}$/, ""),
            toBindingContext(ctx, dataCache),
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("动作触发器执行失败", err);
        }
      }
    };

    return (
      <>
        <Button ref={buttonRef} variant={variant as never} size={size as never} onClick={handleClick}>
          {label}
        </Button>
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{modalConfig.title}</DialogTitle>
              {modalConfig.content ? <DialogDescription>{modalConfig.content}</DialogDescription> : null}
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </>
    );
  },
  Input: ({ node, onFormChange }) => <InputRenderer props={node.props} onChange={onFormChange} />,
  Textarea: ({ node, onFormChange }) => <TextareaRenderer props={node.props} onChange={onFormChange} />,
  Select: ({ node, onFormChange }) => <SelectRenderer props={node.props} onChange={onFormChange} />,
  Switch: ({ node, onFormChange }) => <SwitchRenderer props={node.props} onChange={onFormChange} />,
  Checkbox: ({ node, onFormChange }) => <CheckboxRenderer props={node.props} onChange={onFormChange} />,
  Radio: ({ node, onFormChange }) => <RadioRenderer props={node.props} onChange={onFormChange} />,
  DatePicker: ({ node, onFormChange }) => <DatePickerRenderer props={node.props} onChange={onFormChange} />,
  Table: ({ node, ctx }) => <TableRenderer props={node.props} permissions={ctx.permissions} />,
  Form: ({ node, ctx, children }) => (
    <FormRenderer props={node.props} permissions={ctx.permissions}>
      {children}
    </FormRenderer>
  ),
  AIChat: ({ node }) => <AIChatRenderer props={node.props} />,
  Chart: ({ node }) => <ChartRenderer props={node.props} />,
  RichText: ({ node }) => <RichTextRenderer props={node.props} />,
  FileUpload: ({ node }) => <FileUploadRenderer props={node.props} />,
  Card: ({ node, children }) => <CardRenderer props={node.props}>{children}</CardRenderer>,

  // ---- 6.7 扩展：layout ----
  Grid: ({ node, ctx, dataCache, children }) => (
    <GridRenderer props={resolveProps(node.props, ctx, dataCache)}>{children}</GridRenderer>
  ),
  Flex: ({ node, ctx, dataCache, children }) => (
    <FlexRenderer props={resolveProps(node.props, ctx, dataCache)}>{children}</FlexRenderer>
  ),
  Stack: ({ node, ctx, dataCache, children }) => (
    <StackRenderer props={resolveProps(node.props, ctx, dataCache)}>{children}</StackRenderer>
  ),
  Tabs: ({ node, ctx, dataCache, children }) => (
    <TabsRenderer props={resolveProps(node.props, ctx, dataCache)}>{children}</TabsRenderer>
  ),
  Sidebar: ({ node, ctx, dataCache, children }) => (
    <SidebarRenderer props={resolveProps(node.props, ctx, dataCache)}>{children}</SidebarRenderer>
  ),

  // ---- 6.7 扩展：basic ----
  Slider: ({ node, onFormChange }) => (
    <SliderRenderer props={node.props} onChange={onFormChange} />
  ),

  // ---- 6.7 扩展：data ----
  List: ({ node, ctx, dataCache }) => (
    <ListRenderer props={resolveProps(node.props, ctx, dataCache)} permissions={ctx.permissions} />
  ),
  Pagination: ({ node, ctx, dataCache }) => <PaginationRenderer props={resolveProps(node.props, ctx, dataCache)} />,
  SearchBar: ({ node, onFormChange }) => <SearchBarRenderer props={node.props} onChange={onFormChange} />,
  FilterPanel: ({ node }) => <FilterPanelRenderer props={node.props} />,

  // ---- 6.7 扩展：ai ----
  AICopilot: ({ node }) => <AICopilotRenderer props={node.props} />,
  AIGenerate: ({ node }) => <AIGenerateRenderer props={node.props} />,

  // ---- 6.7 扩展：chart ----
  BarChart: ({ node, ctx, dataCache }) => <BarChartRenderer props={resolveProps(node.props, ctx, dataCache)} />,
  LineChart: ({ node, ctx, dataCache }) => <LineChartRenderer props={resolveProps(node.props, ctx, dataCache)} />,
  PieChart: ({ node, ctx, dataCache }) => <PieChartRenderer props={resolveProps(node.props, ctx, dataCache)} />,
  AreaChart: ({ node, ctx, dataCache }) => <AreaChartRenderer props={resolveProps(node.props, ctx, dataCache)} />,
  MetricCard: ({ node, ctx, dataCache }) => <MetricCardRenderer props={resolveProps(node.props, ctx, dataCache)} />,

  // ---- 6.7 扩展：content ----
  Image: ({ node, ctx, dataCache }) => <ImageRenderer props={resolveProps(node.props, ctx, dataCache)} />,
  Video: ({ node, ctx, dataCache }) => <VideoRenderer props={resolveProps(node.props, ctx, dataCache)} />,
  Markdown: ({ node, ctx, dataCache }) => <MarkdownRenderer props={resolveProps(node.props, ctx, dataCache)} />,

  // ---- 6.7 扩展：motion（自包含 CSS 动效） ----
  MotionGroup: ({ node, ctx, dataCache, children }) => (
    <MotionGroupRenderer props={resolveProps(node.props, ctx, dataCache)}>{children}</MotionGroupRenderer>
  ),
  AnimatedRoute: ({ node, ctx, dataCache, children }) => (
    <AnimatedRouteRenderer props={resolveProps(node.props, ctx, dataCache)}>{children}</AnimatedRouteRenderer>
  ),
  ScrollReveal: ({ node, ctx, dataCache, children }) => (
    <ScrollRevealRenderer props={resolveProps(node.props, ctx, dataCache)}>{children}</ScrollRevealRenderer>
  ),
};

// 登记渲染器到 Widget 体系（BaseWidget.getPageView 经此引用现有渲染函数）
Object.entries(renderers).forEach(([type, renderer]) => {
  registerRenderer(type, renderer as WidgetRenderer);
});

// 动作触发器绑定的默认 showAlert 落地（浏览器环境）
if (typeof window !== "undefined" && typeof window.alert === "function") {
  setAlertHandler((message) => window.alert(message));
}

/**
 * motionPreset 动效接线（6.4 → 8.2）：组件 props.motionPreset 非 none 时，
 * 用动效引擎 applyRecipe 注入 className / style。
 *
 * - entrance 类（trigger=load/scroll-into-view/navigation）：applyRecipe 产出
 *   inline animation 简写，挂载即播放（@keyframes 由 <MotionPresetStyles /> 注入）；
 * - 交互类（trigger=hover/focus/click/error）：applyRecipe 只产出 className，
 *   由 CSS export 中的伪类规则驱动（避免挂载即播放）。
 * - none / 未知预设：原样渲染，不做包裹。
 */
function MotionWrapper({ node, children }: { node: ComponentNode; children: React.ReactNode }) {
  const presetId = node.props?.motionPreset;
  if (!presetId || presetId === "none") {
    return <>{children}</>;
  }
  const preset = getPreset(String(presetId));
  if (!preset) {
    return <>{children}</>;
  }
  const applied = applyRecipe(preset.recipe);
  return (
    <div className={applied.className} style={applied.style} data-motion-preset={preset.id}>
      {children}
    </div>
  );
}

function RuntimeNode({
  node,
  ctx,
  dataCache,
  onFormChange,
}: {
  node: ComponentNode;
  ctx: RuntimeContext;
  dataCache: Record<string, Record<string, unknown>[]>;
  onFormChange?: (name: string, value: FormValue) => void;
}) {
  const Renderer = renderers[node.type];

  // 6.3/6.5：构建 Widget 运行时属性包（default + meta + derived）作为 {{ this.xxx }} 上下文
  const thisCtx = React.useMemo(() => {
    const widget = getWidgetForType(node.type);
    if (!widget) return undefined;
    const resolved = resolvePropsExpression(node.props, toBindingContext(ctx, dataCache));
    return buildRuntimeProperties(widget.propertyDefinitions(), resolved, toBindingContext(ctx, dataCache), DEFAULT_BINDING_FUNCTIONS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.type, node.props, ctx, dataCache]);

  const fullCtx = thisCtx !== undefined ? { ...ctx, this: thisCtx } : ctx;
  const children = node.children?.map((child) => (
    <RuntimeNode key={child.id} node={child} ctx={fullCtx} dataCache={dataCache} onFormChange={onFormChange} />
  ));

  if (!Renderer) {
    return (
      <div className="rounded border border-dashed border-destructive p-2 text-xs text-destructive">
        未知组件：{node.type}
      </div>
    );
  }

  return (
    <RuntimeErrorBoundary nodeType={node.type}>
      <MotionWrapper node={node}>
        <Renderer node={node} ctx={fullCtx} dataCache={dataCache} onFormChange={onFormChange}>
          {children}
        </Renderer>
      </MotionWrapper>
    </RuntimeErrorBoundary>
  );
}

export function RuntimeRenderer({
  node,
  ctx = {},
}: {
  node: ComponentNode;
  ctx?: RuntimeContext;
}) {
  const { cache } = useRuntimeData();

  return (
    <>
      <MotionPresetStyles />
      <RuntimeNode node={node} ctx={ctx} dataCache={cache} />
    </>
  );
}

export function PageRenderer({ page, ctx }: { page: Page; ctx?: RuntimeContext }) {
  return (
    <div className="h-full overflow-auto">
      <RuntimeRenderer node={page.layout} ctx={ctx} />
    </div>
  );
}
